import { useEffect, useRef, useState } from 'react'
import './LazyHCaptcha.css'

/**
 * Drop-in, lazily loaded replacement for `@hcaptcha/react-hcaptcha`.
 *
 * Why: the hCaptcha library and the scripts it injects (api.js, hsw.js,
 * checksiteconfig) cost ~930 KiB and ~1.3 s of main-thread time on mobile.
 * Every form sits far below the fold, so nothing hCaptcha-related is requested
 * until the visitor is about to use the form.
 *
 * Public by design: the hCaptcha *site* key passed via `sitekey` is meant to
 * ship in the client bundle. The secret key is only ever used server-side by
 * the form provider and never appears here.
 */

/** Start loading when the placeholder is this close to the viewport. */
const PRELOAD_MARGIN_PX = 300

/** Interactions inside the enclosing <form> that mean "about to submit". */
const FORM_TRIGGER_EVENTS = Object.freeze(['focusin', 'pointerdown'])

const DEFAULT_LOCALE = 'tr'

const STRINGS = Object.freeze({
  tr: Object.freeze({
    error: 'Güvenlik doğrulaması yüklenemedi. Sayfayı yenileyip tekrar deneyin.',
    reload: 'Sayfayı yenile',
  }),
  en: Object.freeze({
    error: 'The security check could not be loaded. Reload the page and try again.',
    reload: 'Reload page',
  }),
  de: Object.freeze({
    error:
      'Die Sicherheitsprüfung konnte nicht geladen werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
    reload: 'Seite neu laden',
  }),
})

const IDLE_STATE = Object.freeze({ status: 'idle', Component: null })

/** Shared across instances so the chunk is fetched at most once per page. */
let hcaptchaModulePromise = null

/**
 * Dynamically import the hCaptcha React component (emitted by Vite as its own
 * `hcaptchaWidget-<hash>.js` chunk). A failed import is not memoised here, so a
 * later mount may try again; note that Chromium caches the failed module fetch
 * itself, which is why recovery in the UI is a page reload, not an in-page retry.
 *
 * @returns {Promise<import('react').ComponentType<Record<string, unknown>>>}
 */
function loadHCaptchaComponent() {
  if (!hcaptchaModulePromise) {
    hcaptchaModulePromise = import('./hcaptchaWidget.js')
      .then((mod) => mod.default)
      .catch((error) => {
        hcaptchaModulePromise = null
        throw error
      })
  }
  return hcaptchaModulePromise
}

/**
 * A reload also fixes the most likely real cause: a deploy replaced the hashed
 * chunk this (older) page still points at.
 */
function reloadPage() {
  window.location.reload()
}

/**
 * @param {string|undefined} languageOverride
 * @param {string|undefined} hl
 * @returns {{ error: string, reload: string }}
 */
function resolveStrings(languageOverride, hl) {
  const code = String(languageOverride || hl || DEFAULT_LOCALE).slice(0, 2).toLowerCase()
  return STRINGS[code] ?? STRINGS[DEFAULT_LOCALE]
}

/**
 * Flips to true (and stays true) once the container comes within
 * PRELOAD_MARGIN_PX of the viewport, or the visitor focuses / presses anything
 * inside the closest <form>.
 *
 * @param {import('react').RefObject<HTMLElement|null>} containerRef
 * @returns {boolean}
 */
function useLoadTrigger(containerRef) {
  const [isTriggered, setIsTriggered] = useState(
    () => typeof IntersectionObserver === 'undefined'
  )

  useEffect(() => {
    const node = containerRef.current
    if (isTriggered || !node) return undefined

    const trigger = () => setIsTriggered(true)
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) trigger()
      },
      { rootMargin: `${PRELOAD_MARGIN_PX}px 0px` }
    )
    observer.observe(node)

    const form = node.closest('form')
    FORM_TRIGGER_EVENTS.forEach((type) => form?.addEventListener(type, trigger))

    return () => {
      observer.disconnect()
      FORM_TRIGGER_EVENTS.forEach((type) => form?.removeEventListener(type, trigger))
    }
  }, [containerRef, isTriggered])

  return isTriggered
}

/**
 * Loads the hCaptcha component once triggered and exposes the load state.
 *
 * @param {boolean} isTriggered
 * @returns {{
 *   status: 'idle'|'loading'|'ready'|'error',
 *   Component: import('react').ComponentType<Record<string, unknown>>|null
 * }}
 */
function useHCaptchaModule(isTriggered) {
  const [state, setState] = useState(IDLE_STATE)

  useEffect(() => {
    if (!isTriggered) return undefined
    let isActive = true
    loadHCaptchaComponent().then(
      (Component) => {
        if (isActive) setState({ status: 'ready', Component })
      },
      () => {
        // Surfaced to the visitor as an inline alert with a reload button; the
        // form's own "captcha required" check still blocks submission.
        if (isActive) setState({ status: 'error', Component: null })
      }
    )
    return () => {
      isActive = false
    }
  }, [isTriggered])

  const status = state.status === 'idle' && isTriggered ? 'loading' : state.status
  return { status, Component: state.Component }
}

/**
 * Lazily loaded hCaptcha widget with the same props and ref API as
 * `@hcaptcha/react-hcaptcha`'s default export.
 *
 * All props are forwarded unchanged. `ref` (a plain prop in React 19) is passed
 * straight to the real HCaptcha instance once it has loaded, so
 * `ref.current.resetCaptcha()`, `execute()`, `getResponse()` etc. behave exactly
 * as before. Until then `ref.current` is `null`; callers must null-check it
 * (every current call site does). Without a token the form's existing
 * "captcha required" validation blocks submission.
 *
 * @param {Object} props - Any `@hcaptcha/react-hcaptcha` props (sitekey, theme,
 *   size, languageOverride, onVerify, onExpire, ...).
 * @param {import('react').Ref<unknown>} [props.ref]
 * @param {'normal'|'compact'|'invisible'} [props.size]
 * @param {string} [props.languageOverride] - Also selects the fallback copy locale.
 * @param {string} [props.hl] - Locale hint used when languageOverride is absent.
 */
function LazyHCaptcha({ ref, ...hcaptchaProps }) {
  const containerRef = useRef(null)
  const isTriggered = useLoadTrigger(containerRef)
  const { status, Component } = useHCaptchaModule(isTriggered)
  const strings = resolveStrings(hcaptchaProps.languageOverride, hcaptchaProps.hl)
  const sizeClass = hcaptchaProps.size ? ` lazy-hcaptcha--${hcaptchaProps.size}` : ''

  return (
    <div
      ref={containerRef}
      className={`lazy-hcaptcha${sizeClass}`}
      aria-busy={status === 'loading' || undefined}
      data-captcha-status={status}
    >
      {status === 'ready' && Component && <Component ref={ref} {...hcaptchaProps} />}
      {(status === 'idle' || status === 'loading') && (
        <div className="lazy-hcaptcha__placeholder" aria-hidden="true">
          <span className="lazy-hcaptcha__checkbox" />
          <span className="lazy-hcaptcha__bar" />
        </div>
      )}
      {status === 'error' && (
        <div>
          <p className="lazy-hcaptcha__error" role="alert">
            {strings.error}
          </p>
          <button type="button" className="lazy-hcaptcha__reload" onClick={reloadPage}>
            {strings.reload}
          </button>
        </div>
      )}
    </div>
  )
}

export default LazyHCaptcha
